import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';
import { BarChart3, CalendarClock, Database, Loader2, RefreshCw } from 'lucide-react';
import { VisitDailyChart, VisitSourcesList } from './VisitStatsCharts';

export function VisitStatsPanel() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (refresh = false) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/analytics/visit-stats${refresh ? '?refresh=true' : ''}`);
            setStats(data);
            if (refresh) toast.success('Estadísticas de visitas actualizadas');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudieron cargar las visitas');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(false); }, [load]);

    return (
        <Card data-testid="visit-stats-panel">
            <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
                <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <BarChart3 className="h-5 w-5 text-sky-700" />
                        Visitas a la portada
                    </CardTitle>
                    <CardDescription>
                        Sesiones únicas de visita a la página pública. Misma fuente que el contador público (pageviews, caché 5 min).
                    </CardDescription>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => load(true)}
                    disabled={loading}
                    data-testid="visit-stats-refresh-btn"
                >
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                    Actualizar estadísticas
                </Button>
            </CardHeader>
            <CardContent>
                {!stats ? (
                    <p className="text-sm text-slate-500">Cargando…</p>
                ) : stats.sin_datos ? (
                    <p className="text-sm text-slate-500" data-testid="visit-stats-empty">Aún no hay registros de visitas a la portada.</p>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                            {stats.periodos.map((p) => (
                                <div
                                    key={p.key}
                                    className="rounded-xl border border-slate-200 bg-slate-50/60 p-4"
                                    data-testid={`visit-stats-${p.key}`}
                                >
                                    <p className="text-xs text-slate-500">{p.label}</p>
                                    <p className="mt-1 font-mono text-2xl font-bold text-slate-900" data-testid={`visit-stats-${p.key}-value`}>
                                        {p.sesiones.toLocaleString('es-CL')}
                                    </p>
                                    <div className="mt-2">
                                        {p.completo ? (
                                            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Completo</Badge>
                                        ) : (
                                            <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800" title={`Registros desde ${p.desde}`}>
                                                Parcial · desde {p.desde}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5 text-xs text-slate-600">
                            <span className="inline-flex items-center gap-1.5" data-testid="visit-stats-updated">
                                <CalendarClock className="h-3.5 w-3.5" />
                                Última actualización: <strong>{stats.actualizado_en_texto}</strong> hrs (America/Santiago)
                                {stats.desde_cache && <span className="text-slate-400">· caché {stats.cache_edad_seg}s</span>}
                            </span>
                            <span className="inline-flex items-center gap-1.5" data-testid="visit-stats-since">
                                <Database className="h-3.5 w-3.5" />
                                Datos confiables desde: <strong>{stats.datos_desde_fecha}</strong>
                            </span>
                        </div>
                        {stats.detalle && (
                            <div className="mt-5 grid gap-4 lg:grid-cols-5">
                                <div className="lg:col-span-3"><VisitDailyChart diario={stats.detalle.diario_30d} /></div>
                                <div className="lg:col-span-2"><VisitSourcesList fuentes={stats.detalle.fuentes} /></div>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

export default VisitStatsPanel;
